import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section } from "@/components/CalcUI";
import { useUnits, L } from "@/context/UnitsContext";
import { useUnitInputs, useOnUnitsChange } from "@/lib/units";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function Capacities() {
  const { units } = useUnits();
  const u = L(units);

  const [pipe, setPipe] = useUnitInputs({ id: "4.276" }, { id: "diameter" });
  const [pipeOut, setPipeOut] = useState(null);

  const [ann, setAnn] = useUnitInputs({ hole_id: "8.5", pipe_od: "5.0" }, { hole_id: "diameter", pipe_od: "diameter" });
  const [annOut, setAnnOut] = useState(null);

  const [vol, setVol] = useUnitInputs({ capacity: "0.01776", length: "10000" }, { capacity: "capacity", length: "length" });
  const [volOut, setVolOut] = useState(null);

  const [stk, setStk] = useUnitInputs({ volume: "180", pump_output: "0.117" }, { volume: "volume", pump_output: "volume" });
  const [stkOut, setStkOut] = useState(null);
  useOnUnitsChange(() => {
    setPipeOut(null);
    setAnnOut(null);
    setVolOut(null);
    setStkOut(null);
  });

  const run = async (calc, inp, setter) => {
    try {
      const r = await runCalc(calc, Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)])), units);
      setter(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="capacities-page">
      <Section title="Capacities & Volumes" subtitle="Pipe and annular capacities in field or metric units, volume from length, and pump strokes to displace a given volume.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="pipe-cap-card" title="Pipe Internal Capacity" formula={units === "field" ? "Cap = ID²/1029.4" : "Cap = π/4·D² / 1000"}>
            <NumField testid="pipe-id" label="Pipe ID" unit={u.diameter} value={pipe.id} onChange={(v) => setPipe({ id: v })} />
            <div className="mt-4"><PrimaryButton testid="pipe-run" onClick={() => run("pipe_capacity", pipe, setPipeOut)}>Calculate</PrimaryButton></div>
            {pipeOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Capacity" value={pipeOut.capacity} unit={pipeOut.unit} emphasis /></div>}
          </CalcCard>

          <CalcCard testid="ann-cap-card" title="Annular Capacity" formula={units === "field" ? "Cap = (D²−d²)/1029.4" : "Cap = π/4·(D²−d²)/1000"}>
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="ann-hole" label="Hole/Casing ID" unit={u.diameter} value={ann.hole_id} onChange={(v) => setAnn({ ...ann, hole_id: v })} />
              <NumField testid="ann-pipe" label="Pipe OD" unit={u.diameter} value={ann.pipe_od} onChange={(v) => setAnn({ ...ann, pipe_od: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="ann-run" onClick={() => run("annular_capacity", ann, setAnnOut)}>Calculate</PrimaryButton></div>
            {annOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Capacity" value={annOut.capacity} unit={annOut.unit} emphasis /></div>}
          </CalcCard>

          <CalcCard testid="vol-card" title="Volume from Length" formula="V = Cap · L">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="vol-cap" label="Capacity" unit={u.capacity} value={vol.capacity} onChange={(v) => setVol({ ...vol, capacity: v })} />
              <NumField testid="vol-len" label="Length" unit={u.length} value={vol.length} onChange={(v) => setVol({ ...vol, length: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="vol-run" onClick={() => run("volume", vol, setVolOut)}>Calculate</PrimaryButton></div>
            {volOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Volume" value={volOut.volume} unit={u.volume} emphasis /></div>}
          </CalcCard>

          <CalcCard testid="stk-card" title="Strokes to Displace" formula="Stk = ceil(V / pump_output)">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="stk-vol" label="Volume" unit={u.volume} value={stk.volume} onChange={(v) => setStk({ ...stk, volume: v })} />
              <NumField testid="stk-pump" label="Pump output" unit={u.volume + "/stk"} value={stk.pump_output} onChange={(v) => setStk({ ...stk, pump_output: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="stk-run" onClick={() => run("strokes", stk, setStkOut)}>Calculate</PrimaryButton></div>
            {stkOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Strokes" value={stkOut.strokes} unit="stk" emphasis /></div>}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
